/**
 * IA Calculator Utility Functions
 * Handles API communication and score calculation for the Improvement Activities calculator
 */

import { invalidatePracticeScoring } from '@/lib/query-keys';

// Get API URL for database calls
const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || 'http://localhost:3001';
};

// Get auth token for API calls
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface IASubcategory {
  id: string;
  name: string;
  prefix: string;
}

export interface IAValidationCriteria {
  measureId: string;
  subcategoryName: string | null;
  activityName: string;
  activityDescription: string;
  validationDocumentation: string;
  firstPerformanceYear: number;
}

export interface IAActivity {
  measureId: string;
  category: string;
  title: string;
  description: string;
  subcategoryId: string;
  weight: string;
  metricType: string;
  firstPerformanceYear: number;
  lastPerformanceYear: number | null;
  validationCriteria?: IAValidationCriteria;
}

export interface IASelection {
  id: number;
  practice_id: number;
  year: number;
  ia_activity_id: string;
  npi: string | null;
  selected_at: string;
  selected_by: number;
  activity?: IAActivity;
}

export interface IAAttestation {
  id: number;
  practice_id: number;
  year: number;
  ia_activity_id: string;
  npi: string | null;
  status: 'not_started' | 'in_progress' | 'completed';
  performance_start: string | null;
  performance_end: string | null;
  participating_npis: string[];
  clinician_participation_count: number;
  clinician_total_count: number;
  documentation_notes: string | null;
  is_attested: boolean;
  attested_by_name: string | null;
  attested_by_user_id: number | null;
  attested_at: string | null;
  is_valid: boolean;
  validation_errors: string[];
  created_at: string;
  updated_at: string;
  document_count?: number;
  activity?: IAActivity;
  documents?: IADocument[];
}

export interface IADocument {
  id: number;
  attestation_id: number;
  file_name: string;
  file_original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  category: string | null;
  uploaded_at: string;
  uploaded_by: number;
}

export interface IARequirements {
  activitiesRequired: number;
  pointsPerActivity: number;
  maxPoints: number;
  isSpecialStatus: boolean;
  statusLabel: string | null;
  practiceSettings?: {
    is_small_practice: boolean;
    is_hpsa_rural: boolean;
    is_non_patient_facing: boolean;
  };
}

export interface IAScore {
  completedCount: number;
  requiredCount: number;
  pointsPerActivity: number;
  totalScore: number;
  maxScore: number;
  isComplete: boolean;
  isSpecialStatus: boolean;
  statusLabel: string | null;
  activities: {
    activityId: string;
    status: string;
    isValid: boolean;
    validationErrors: string[];
    points: number;
  }[];
}

export interface IAActivitiesResponse {
  activities: IAActivity[];
  total: number;
  subcategories: IASubcategory[];
}

export interface IAAttestationInput {
  npi?: string | null;
  status?: 'not_started' | 'in_progress' | 'completed';
  performance_start?: string | null;
  performance_end?: string | null;
  participating_npis?: string[];
  clinician_participation_count?: number;
  clinician_total_count?: number;
  documentation_notes?: string | null;
  is_attested?: boolean;
  attested_by_name?: string | null;
}

// Document categories
export const DOCUMENT_CATEGORIES = [
  { value: 'training', label: 'Training Records' },
  { value: 'policy', label: 'Policy Document' },
  { value: 'workflow', label: 'Workflow Diagram' },
  { value: 'report', label: 'Report/Data' },
  { value: 'screenshot', label: 'Screenshot/Evidence' },
  { value: 'other', label: 'Other' }
] as const;

// ============================================================================
// API Functions - Reference Data
// ============================================================================

/**
 * Fetch all IA activities with optional subcategory filter
 */
export async function getIAActivities(
  practiceId: string,
  options?: { subcategory?: string; search?: string; year?: number }
): Promise<IAActivitiesResponse | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    let url = `${apiUrl}/api/practices/${practiceId}/ia/activities`;
    const params = new URLSearchParams();
    params.append('year', String(options?.year || 2025));
    if (options?.subcategory) params.append('subcategory', options.subcategory);
    if (options?.search) params.append('search', options.search);
    url += `?${params.toString()}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA activities: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA activities:', error);
    return null;
  }
}

/**
 * Fetch subcategories for filter dropdown
 */
export async function getIASubcategories(practiceId: string): Promise<IASubcategory[]> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(`${apiUrl}/api/practices/${practiceId}/ia/subcategories`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA subcategories: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA subcategories:', error);
    return [];
  }
}

/**
 * Fetch practice-specific IA requirements
 */
export async function getIARequirements(
  practiceId: string,
  options?: { year?: number }
): Promise<IARequirements | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    const response = await fetch(`${apiUrl}/api/practices/${practiceId}/ia/requirements?year=${year}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA requirements: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA requirements:', error);
    return null;
  }
}

// ============================================================================
// API Functions - Selections
// ============================================================================

/**
 * Fetch practice's selected IAs
 */
export async function getIASelections(
  practiceId: string,
  options?: { year?: number; npi?: string }
): Promise<IASelection[]> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/selections?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA selections: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA selections:', error);
    return [];
  }
}

/**
 * Add IA to selections
 */
export async function addIASelection(
  practiceId: string,
  activityId: string,
  options?: { year?: number; npi?: string }
): Promise<IASelection | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    const response = await fetch(
      `${apiUrl}/api/practices/${practiceId}/ia/selections?year=${year}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activityId, npi: options?.npi }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to add IA selection: ${response.status}`);
    }
    
    const result = await response.json();
    invalidatePracticeScoring(practiceId);
    return result;
  } catch (error) {
    console.error('Error adding IA selection:', error);
    return null;
  }
}

/**
 * Remove IA from selections
 */
export async function removeIASelection(
  practiceId: string,
  activityId: string,
  options?: { year?: number; npi?: string }
): Promise<boolean> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/selections/${activityId}?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to remove IA selection: ${response.status}`);
    }
    
    invalidatePracticeScoring(practiceId);
    return true;
  } catch (error) {
    console.error('Error removing IA selection:', error);
    return false;
  }
}

/**
 * Reset ALL IA selections and attestations for a practice/year
 */
export async function resetAllIA(
  practiceId: string,
  options?: { year?: number; npi?: string }
): Promise<{ success: boolean; deletedAttestations: number; deletedSelections: number }> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();

    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/reset-all?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to reset IA data: ${response.status}`);
    }

    const result = await response.json();
    invalidatePracticeScoring(practiceId);
    return result;
  } catch (error) {
    console.error('Error resetting IA data:', error);
    return { success: false, deletedAttestations: 0, deletedSelections: 0 };
  }
}

// ============================================================================
// API Functions - Attestations
// ============================================================================

/**
 * Fetch all attestations for practice
 */
export async function getIAAttestations(
  practiceId: string,
  options?: { year?: number; npi?: string }
): Promise<IAAttestation[]> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/attestations?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA attestations: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA attestations:', error);
    return [];
  }
}

/**
 * Fetch single attestation
 */
export async function getIAAttestation(
  practiceId: string,
  activityId: string,
  options?: { year?: number; npi?: string }
): Promise<IAAttestation | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/attestations/${activityId}?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to fetch IA attestation: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA attestation:', error);
    return null;
  }
}

/**
 * Save IA attestation (auto-save)
 */
export async function saveIAAttestation(
  practiceId: string,
  activityId: string,
  data: IAAttestationInput,
  options?: { year?: number }
): Promise<IAAttestation> {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  const year = options?.year || 2025;
  const response = await fetch(
    `${apiUrl}/api/practices/${practiceId}/ia/attestations/${activityId}?year=${year}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('IA Attestation save failed:', response.status, errorText);
    throw new Error(`Failed to save IA attestation: ${response.status}`);
  }
  
  const result = await response.json();
  invalidatePracticeScoring(practiceId);
  return result;
}

// ============================================================================
// API Functions - Scoring
// ============================================================================

/**
 * Fetch IA score for practice
 */
export async function getIAScore(
  practiceId: string,
  options?: { year?: number; npi?: string }
): Promise<IAScore | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/score?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch IA score: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA score:', error);
    return null;
  }
}

// ============================================================================
// Client-Side Score Calculation
// ============================================================================

/**
 * Calculate IA score on the client side for real-time updates
 */
export function calculateIAScoreClient(
  completedCount: number,
  isSpecialStatus: boolean
): { totalScore: number; requiredCount: number; pointsPerActivity: number; isComplete: boolean } {
  const requiredCount = isSpecialStatus ? 1 : 2;
  const pointsPerActivity = isSpecialStatus ? 40 : 20;
  const totalScore = Math.min(completedCount * pointsPerActivity, 40);
  
  return {
    totalScore,
    requiredCount,
    pointsPerActivity,
    isComplete: completedCount >= requiredCount
  };
}

// ============================================================================
// Validation Functions
// ============================================================================

export interface IAValidationError {
  field: string;
  message: string;
}

/**
 * Validate performance period (minimum 90 days, within PY)
 */
export function validatePerformancePeriod(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  year: number = 2025
): IAValidationError[] {
  const errors: IAValidationError[] = [];
  
  if (!startDate || !endDate) return errors; // Not required until completion
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const pyStart = new Date(`${year}-01-01`);
  const pyEnd = new Date(`${year}-12-31`);
  
  // Check dates are within performance year
  if (start < pyStart || start > pyEnd) {
    errors.push({
      field: 'performance_start',
      message: 'Start date must be within performance year'
    });
  }
  
  if (end < pyStart || end > pyEnd) {
    errors.push({
      field: 'performance_end',
      message: 'End date must be within performance year'
    });
  }
  
  // Check minimum 90 days
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 90) {
    errors.push({
      field: 'performance_period',
      message: 'Minimum 90 continuous days required'
    });
  }
  
  return errors;
}

/**
 * Validate clinician participation (> 50% for group)
 */
export function validateClinicianParticipation(
  participationCount: number,
  totalCount: number,
  isGroupLevel: boolean
): IAValidationError | null {
  if (!isGroupLevel) return null; // Only for group level
  if (totalCount === 0) return null; // No clinicians yet
  
  const percentage = (participationCount / totalCount) * 100;
  
  if (percentage <= 50) {
    return {
      field: 'clinician_participation',
      message: 'More than 50% of clinicians must participate'
    };
  }
  
  return null;
}

/**
 * Validate documentation exists
 */
export function validateDocumentation(
  notes: string | null | undefined,
  documentCount: number
): IAValidationError | null {
  const hasNotes = notes && notes.trim().length > 0;
  const hasDocuments = documentCount > 0;
  
  if (!hasNotes && !hasDocuments) {
    return {
      field: 'documentation',
      message: 'Documentation required for audit purposes'
    };
  }
  
  return null;
}

/**
 * Validate attestation confirmation
 */
export function validateAttestation(isAttested: boolean): IAValidationError | null {
  if (!isAttested) {
    return {
      field: 'attestation',
      message: 'Please confirm attestation'
    };
  }
  return null;
}

/**
 * Get all validation errors for an attestation
 */
export function getAllIAValidationErrors(
  attestation: IAAttestation,
  isGroupLevel: boolean = true
): IAValidationError[] {
  const errors: IAValidationError[] = [];
  
  // Performance period
  const periodErrors = validatePerformancePeriod(
    attestation.performance_start,
    attestation.performance_end,
    attestation.year
  );
  errors.push(...periodErrors);
  
  // Clinician participation (group only)
  const participationError = validateClinicianParticipation(
    attestation.clinician_participation_count,
    attestation.clinician_total_count,
    isGroupLevel
  );
  if (participationError) errors.push(participationError);
  
  // Documentation
  const docError = validateDocumentation(
    attestation.documentation_notes,
    attestation.document_count || 0
  );
  if (docError) errors.push(docError);
  
  // Attestation confirmation
  const attestError = validateAttestation(attestation.is_attested);
  if (attestError) errors.push(attestError);
  
  return errors;
}

// ============================================================================
// API Functions - Documents
// ============================================================================

// Re-export IADocument as IAAttestationDocument for component compatibility
export type IAAttestationDocument = IADocument;

/**
 * Upload document for an attestation
 */
export async function uploadIADocument(
  practiceId: string,
  attestationId: number,
  file: File,
  category: string
): Promise<IADocument> {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('attestationId', attestationId.toString());
  formData.append('category', category);
  
  const response = await fetch(
    `${apiUrl}/api/practices/${practiceId}/ia/documents/upload`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }
  );
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('Document upload failed:', response.status, errorText);
    throw new Error(`Failed to upload document: ${response.status}`);
  }
  
  const result = await response.json();
  return result.document;
}

/**
 * Get presigned URL for document download/view
 */
export async function getIADocumentUrl(
  practiceId: string,
  documentId: number
): Promise<string> {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  const response = await fetch(
    `${apiUrl}/api/practices/${practiceId}/ia/documents/${documentId}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to get document URL: ${response.status}`);
  }
  
  const result = await response.json();
  return result.url;
}

/**
 * Download document file
 */
export async function downloadIADocument(
  practiceId: string,
  document: IADocument
): Promise<void> {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  const response = await fetch(
    `${apiUrl}/api/practices/${practiceId}/ia/documents/${document.id}?download=true`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.status}`);
  }
  
  // Create a blob and download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = document.file_original_name;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Get all documents for an attestation
 */
export async function getIADocuments(
  practiceId: string,
  activityId: string,
  options?: { year?: number; npi?: string }
): Promise<IADocument[]> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const year = options?.year || 2025;
    let url = `${apiUrl}/api/practices/${practiceId}/ia/attestations/${activityId}/documents?year=${year}`;
    if (options?.npi) url += `&npi=${options.npi}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch documents: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching IA documents:', error);
    return [];
  }
}

/**
 * Delete document
 */
export async function deleteIADocument(
  practiceId: string,
  documentId: number
): Promise<boolean> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/practices/${practiceId}/ia/documents/${documentId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting IA document:', error);
    return false;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate days between two dates
 */
export function calculateDaysBetween(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number | null {
  if (!startDate || !endDate) return null;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate participation percentage
 */
export function calculateParticipationPercentage(
  participationCount: number,
  totalCount: number
): number {
  if (totalCount === 0) return 0;
  return Math.round((participationCount / totalCount) * 100);
}

/**
 * Get status label for display
 */
export function getStatusLabel(status: IAAttestation['status']): string {
  switch (status) {
    case 'not_started':
      return 'Not Started';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    default:
      return status;
  }
}

/**
 * Get status color class
 */
export function getStatusColorClass(status: IAAttestation['status']): string {
  switch (status) {
    case 'not_started':
      return 'text-gray-500';
    case 'in_progress':
      return 'text-amber-600';
    case 'completed':
      return 'text-emerald-600';
    default:
      return 'text-gray-500';
  }
}

/**
 * Get status badge variant
 */
export function getStatusBadgeVariant(status: IAAttestation['status']): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'not_started':
      return 'outline';
    case 'in_progress':
      return 'secondary';
    case 'completed':
      return 'default';
    default:
      return 'outline';
  }
}

